import moment from 'moment';

import { comparisonModes } from './diffViewerQuery';
import { differenceSides, sizeBuckets } from './diffViewerResponses';

// Differences are recorded in UTC and stay UTC on this screen, so every instant is formatted with
// moment.utc and never through a local-time conversion.
const utcDateTimeFormat = "DD/MM/YYYY HH:mm:ss";

const modeLabels = {
    [comparisonModes.legacyVersusShadow]: "Legacy v RE2",
    [comparisonModes.underTest]: "Under test"
};

const sideLabels = {
    [differenceSides.value]: "Value difference",
    [differenceSides.legacyOnly]: "Legacy only",
    [differenceSides.underTestOnly]: "RE2 only"
};

const bucketLabels = {
    [sizeBuckets.none]: "No gap",
    [sizeBuckets.exactHour]: "Exactly 1 hour",
    [sizeBuckets.underMinute]: "Under a minute",
    [sizeBuckets.underHour]: "Under an hour",
    [sizeBuckets.overHour]: "Over an hour"
};

export const untriagedText = "Untriaged";
export const provisionalTagText = "Provisional · awaiting a rerun";
export const readFailedHeading = "The differences could not be loaded";
export const readFailedMessage = "The read failed or timed out. Try again.";
export const retryText = "Try again";
const noCounterpartText = "No counterpart found";

export function formatUtcDateTime(value) {
    if (value == null) {
        return "";
    }

    return moment.utc(value).format(utcDateTimeFormat);
}


export function formatValue(value) {
    if (value == null) {
        return "";
    }

    return `${value}`;
}

export function formatMode(mode) {
    return modeLabels[mode] ?? formatValue(mode);
}

export function formatSide(side) {
    return sideLabels[side] ?? formatValue(side);
}

export function formatSizeBucket(sizeBucket) {
    return bucketLabels[sizeBucket] ?? formatValue(sizeBucket);
}

export function formatCode(code, subCode) {
    if (subCode == null || subCode === "") {
        return formatValue(code);
    }

    return `${formatValue(code)} ${subCode}`;
}

export function formatAccount(driver) {
    return [formatValue(driver?.accountName), formatValue(driver?.memberNumber)]
        .filter((part) => part !== "")
        .join(" - ");
}

function formatSignedSeconds(seconds) {
    if (seconds > 0) {
        return `+${seconds}`;
    }

    return `${seconds}`;
}

// The gap is signed from the legacy side, so a positive gap means the legacy value is the greater.
export function formatGap(difference) {
    if (difference.secondsDifference == null) {
        return "";
    }

    return `legacy − RE2 = ${formatSignedSeconds(difference.secondsDifference)} s`;
}

// A one-sided difference is only evidence alongside what, if anything, the other engine produced near it.
export function formatCounterpart(difference) {
    if (difference.side === differenceSides.value) {
        return "";
    }

    if (difference.counterpartOccurredAt == null) {
        return noCounterpartText;
    }

    return `Counterpart on the other side at ${formatUtcDateTime(difference.counterpartOccurredAt)} (UTC)`;
}

export function formatLabel(difference) {
    if (difference.signOff != null) {
        return formatValue(difference.signOff.labelText);
    }

    if (difference.autoLabel != null) {
        return formatValue(difference.autoLabel.text);
    }

    return untriagedText;
}

export function formatLabelReason(difference) {
    if (difference.signOff != null) {
        return formatValue(difference.signOff.reason);
    }

    if (difference.autoLabel != null) {
        return formatValue(difference.autoLabel.rootCause);
    }

    return "";
}

export function formatLabelSource(difference) {
    if (difference.signOff != null) {
        return formatValue(difference.signOff.source);
    }

    if (difference.autoLabel != null) {
        return formatValue(difference.autoLabel.source);
    }

    return "";
}

// The name comes from the synced user record, so an actor the User microservice has not synced has none.
export function formatSigner(signOff) {
    if (signOff?.signedOffBy != null) {
        return signOff.signedOffBy;
    }

    if (signOff?.signedOffByUserId != null) {
        return `user ${signOff.signedOffByUserId}`;
    }

    return "";
}

// A driver with no current employee record comes back unnamed, so the id is all that identifies them.
export function formatDriverName(driver) {
    if (driver?.name != null && driver.name !== "") {
        return driver.name;
    }

    return `Employee ${formatValue(driver?.employeeId)}`;
}

export function formatSignedOff(signOff) {
    return `Signed off by ${formatSigner(signOff)} on ${formatUtcDateTime(signOff.signedOffAt)} (UTC)`;
}
